// =============================================================================
// MobileInventorySheet.tsx
// Bottom sheet shown when the user taps an inventory item. Header (item name +
// close), big "Available" stat, context line (department · location), Issue /
// Return action buttons, and a recent-activity list. Issue and Return open
// sub-sheets that stack on top.
//
// Hooks-at-top, derived-values-after, early-return-last (D73). The sheet is
// mounted permanently by MobileLayout — `selectedItem` toggles between null
// (closed) and an item (open). Hook count must stay constant across renders.
// =============================================================================

import React, { useMemo, useState } from 'react';

import { StaffMember, Department, InventoryLogEntry } from '../../lib/types';

import { S, closeSheetAnim }    from './mobileStyles';
import MobileInventoryIssueSheet  from './MobileInventoryIssueSheet';
import MobileInventoryReturnSheet from './MobileInventoryReturnSheet';

// ── TYPES ────────────────────────────────────────────────────────────

interface MobileInventorySheetProps {
  selectedItem: any;
  setSelectedItem: (item: any) => void;
  invLog: InventoryLogEntry[];
  staffDirectory: StaffMember[];
  departments: Department[];
  lowThreshold: number;
  onInventoryChanged: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  closingSheet: string | null;
  setClosingSheet: (v: string | null) => void;
  onViewAllHistory: (itemId: string, itemName: string) => void;
}

// ── HELPERS ──────────────────────────────────────────────────────────

// Parse staff name from an ISSUE/RETURN notes string.
// Notes format produced by Issue/Return sub-sheets: "Issued to: {name} — {optional}"
// (also produced by the previous sheet version and the desktop UI).
function parseStaffFromNotes(notes: string | undefined | null): string | null {
  if (!notes) return null;
  const m = notes.match(/(?:Issued to|Returned by):\s*([^—]+?)(?:\s*—|$)/i);
  return m ? m[1].trim() : null;
}

// Short, relative-time for recent activity list. Falls back to date for anything older than ~30 days.
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!t) return '';
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Build the middle-column description for a log entry.
function describeEntry(entry: InventoryLogEntry): string {
  const staff = parseStaffFromNotes(entry.notes);
  switch (entry.action) {
    case 'ISSUE':      return staff ? `Issued to ${staff}` : 'Issued';
    case 'RETURN':     return staff ? `Returned by ${staff}` : 'Returned';
    case 'ADD':        return 'Received';
    case 'DELETE':     return 'Deleted';
    case 'RENAME':     return 'Renamed';
    case 'UPDATE':     return 'Updated';
    case 'MERGE_FROM': return 'Merged from';
    case 'MERGE_INTO': return 'Merged into';
    default:           return entry.action;
  }
}

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileInventorySheet({
  selectedItem, setSelectedItem, invLog, staffDirectory, departments, lowThreshold,
  onInventoryChanged, showToast, closingSheet, setClosingSheet, onViewAllHistory,
}: MobileInventorySheetProps) {

  // ── HOOKS — must run unconditionally on every render ─────────────
  // The sheet is mounted permanently by MobileLayout; selectedItem
  // toggles between null (closed) and an item (open). Every hook below
  // runs on every render so the hook count stays constant — Rules of
  // Hooks. The early `return null` for the closed state lives AFTER
  // the hook block.
  const [subSheet, setSubSheet] = useState<'none' | 'issue' | 'return'>('none');

  const itemId: string | null = selectedItem?.id ?? null;

  // All log entries for this item (used for history list, total counts, and per-staff outstanding).
  const itemLog = useMemo(
    () => itemId ? invLog.filter(e => e.item_id === itemId) : [],
    [invLog, itemId]
  );

  // Total quantity currently outstanding (issued minus returned) across all staff for this item.
  // Hides/reveals the Return button per spec.
  const outstandingTotal = useMemo(() => {
    let out = 0;
    for (const e of itemLog) {
      if (e.action === 'ISSUE') out += e.quantity;
      else if (e.action === 'RETURN') out -= e.quantity;
    }
    return Math.max(0, out);
  }, [itemLog]);

  // Per-staff outstanding map — passed into the Return sub-sheet so it can
  // restrict the staff dropdown and per-staff qty cap.
  const outstandingByStaff = useMemo(() => {
    const byStaff: Record<string, number> = {};
    for (const e of itemLog) {
      const name = parseStaffFromNotes(e.notes);
      if (!name) continue;
      if (e.action === 'ISSUE') byStaff[name] = (byStaff[name] || 0) + e.quantity;
      else if (e.action === 'RETURN') byStaff[name] = (byStaff[name] || 0) - e.quantity;
    }
    for (const k of Object.keys(byStaff)) {
      if (byStaff[k] <= 0) delete byStaff[k];
    }
    return byStaff;
  }, [itemLog]);

  // Last-10 history entries (already sorted newest-first by backend).
  const recent = useMemo(() => itemLog.slice(0, 10), [itemLog]);

  // ── Early return for closed state ────────────────────────────────
  if (!selectedItem) return null;
  const item = selectedItem;

  // ── Derived (non-hook) values ────────────────────────────────────
  const hasMore = itemLog.length > 10;
  const isLow = item.available <= (lowThreshold ?? 5);
  const stockColour = isLow ? 'var(--red)' : 'var(--accent)';
  const deptName = departments.find(d => d.id === item.department_id)?.name || '—';
  const contextLine = item.location ? `${deptName} · ${item.location}` : deptName;

  const closeSheet = () => closeSheetAnim(setClosingSheet, setSelectedItem, 'invItem');

  const handleViewAll = () => {
    // Close the detail sheet, then switch tab + pre-filter history to this item.
    setClosingSheet('invItem');
    setTimeout(() => {
      setClosingSheet(null);
      const id = item.id;
      const name = item.name;
      setSelectedItem(null);
      onViewAllHistory(id, name);
    }, 350);
  };

  const handleSubSheetDone = () => {
    setSubSheet('none');
    onInventoryChanged();
  };

  return (
    <>
      <div
        style={S.sheet}
        className={closingSheet === 'invItem' ? 'm-sheet-closing' : 'm-sheet-backdrop'}
        onClick={closeSheet}
      >
        <div
          style={{ ...S.sheetContent, maxHeight: '90vh' }}
          className="m-sheet-content"
          onClick={e => e.stopPropagation()}
        >
          <div style={S.handle as any} />

          {/* Row 1 — Header */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            padding: '4px 20px 12px', gap: 12,
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', lineHeight: 1.2, flex: 1, wordBreak: 'break-word' }}>
              {item.name}
            </div>
            <button
              onClick={closeSheet}
              aria-label="Close"
              style={{
                width: 44, height: 44, minWidth: 44, minHeight: 44,
                background: 'var(--surface2)', border: 'none', borderRadius: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0,
              }}
            >
              <svg viewBox="0 0 20 20" style={{ width: 16, height: 16, fill: 'none', stroke: 'var(--text2)', strokeWidth: 2, strokeLinecap: 'round' }}>
                <path d="M4 4l12 12M16 4L4 16" />
              </svg>
            </button>
          </div>

          {/* Row 2 — Stock headline */}
          <div style={{ padding: '0 20px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: stockColour, lineHeight: 1 }}>{item.available}</span>
            <span style={{ fontSize: 'var(--m-text-meta)', color: 'var(--text2)' }}>available</span>
          </div>

          {/* Row 3 — Context */}
          <div style={{ padding: '4px 20px 16px', fontSize: 'var(--m-text-label)', color: 'var(--text2)' }}>
            {contextLine}
          </div>

          {/* Row 4 — Primary actions */}
          <div style={{ padding: '0 20px 16px', display: 'flex', gap: 8 }}>
            <button
              onClick={() => setSubSheet('issue')}
              style={{
                flex: 1, minHeight: 48,
                background: 'var(--accent)', color: '#fff',
                border: 'none', borderRadius: 12,
                fontSize: 15, fontWeight: 600, fontFamily: 'inherit',
                cursor: 'pointer',
              }}
            >
              Issue
            </button>
            {outstandingTotal > 0 && (
              <button
                onClick={() => setSubSheet('return')}
                style={{
                  flex: 1, minHeight: 48,
                  background: 'var(--surface2)', color: 'var(--text)',
                  border: 'none', borderRadius: 12,
                  fontSize: 15, fontWeight: 600, fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Return
              </button>
            )}
          </div>

          {/* Row 5 — History */}
          <div style={{ padding: '8px 20px 20px', borderTop: '0.5px solid var(--border)' }}>
            <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text2)', fontWeight: 600, marginTop: 12, marginBottom: 8 }}>
              Recent activity
            </div>

            {recent.length === 0 ? (
              <div style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', textAlign: 'center', padding: '20px 0' }}>
                No activity yet
              </div>
            ) : (
              <div>
                {recent.map(entry => {
                  const isNeg = entry.action === 'ISSUE' || entry.action === 'DELETE' || entry.action === 'MERGE_FROM';
                  const sign = isNeg ? '−' : '+';
                  return (
                    <div key={entry.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 0', borderBottom: '0.5px solid var(--border)',
                    }}>
                      <span style={{
                        fontSize: 'var(--m-text-label)', fontWeight: 700,
                        color: isNeg ? 'var(--red)' : 'var(--accent)',
                        minWidth: 36, textAlign: 'right',
                      }}>
                        {sign}{entry.quantity}
                      </span>
                      <span style={{ flex: 1, fontSize: 'var(--m-text-label)', color: 'var(--text)' }}>
                        {describeEntry(entry)}
                      </span>
                      <span style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                        {relativeTime(entry.timestamp)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {hasMore && (
              <button
                onClick={handleViewAll}
                style={{
                  display: 'block', marginTop: 12,
                  background: 'none', border: 'none',
                  color: 'var(--accent)', fontSize: 'var(--m-text-label)', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0',
                  textAlign: 'left',
                }}
              >
                View all
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sub-sheets render above the detail sheet */}
      {subSheet === 'issue' && (
        <MobileInventoryIssueSheet
          item={item}
          staffDirectory={staffDirectory}
          onClose={() => setSubSheet('none')}
          onDone={handleSubSheetDone}
          showToast={showToast}
        />
      )}
      {subSheet === 'return' && (
        <MobileInventoryReturnSheet
          item={item}
          outstandingByStaff={outstandingByStaff}
          onClose={() => setSubSheet('none')}
          onDone={handleSubSheetDone}
          showToast={showToast}
        />
      )}
    </>
  );
}
